import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { isUnverifiedPartner } from "../lib/unverifiedPartners";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Sparkles,
  BadgeDollarSign,
  Truck,
  MapPin,
  Heart,
  Globe2,
  LayoutDashboard,
  Check,
  X,
  ArrowRight,
  Utensils,
  Store,
  Soup,
  Coffee,
  CheckCircle2,
  Send,
} from "lucide-react";

const BUSINESS_TYPES = [
  { code: "restaurant", key: "partners.form.type.restaurant", icon: Utensils },
  { code: "epicerie", key: "partners.form.type.epicerie", icon: Store },
  { code: "traiteur", key: "partners.form.type.traiteur", icon: Soup },
  { code: "cafe_bar", key: "partners.form.type.cafe_bar", icon: Coffee },
  { code: "autre", key: "partners.form.type.autre", icon: Sparkles },
];

const BENEFITS = [
  { icon: BadgeDollarSign, titleKey: "partners.b1t", descKey: "partners.b1d", tint: "#E63946" },
  { icon: Truck, titleKey: "partners.b2t", descKey: "partners.b2d", tint: "#F4A261" },
  { icon: MapPin, titleKey: "partners.b3t", descKey: "partners.b3d", tint: "#2A9D8F" },
  { icon: Heart, titleKey: "partners.b4t", descKey: "partners.b4d", tint: "#E63946" },
  { icon: Globe2, titleKey: "partners.b5t", descKey: "partners.b5d", tint: "#264653" },
  { icon: LayoutDashboard, titleKey: "partners.b6t", descKey: "partners.b6d", tint: "#F4A261" },
];

const FAQ_KEYS = [
  { q: "partners.faq.q1", a: "partners.faq.a1" },
  { q: "partners.faq.q2", a: "partners.faq.a2" },
  { q: "partners.faq.q3", a: "partners.faq.a3" },
  { q: "partners.faq.q4", a: "partners.faq.a4" },
  { q: "partners.faq.q5", a: "partners.faq.a5" },
  { q: "partners.faq.q6", a: "partners.faq.a6" },
];

const initialForm = {
  business_name: "",
  business_type: "restaurant",
  address: "",
  city: "",
  contact_name: "",
  phone: "",
  email: "",
  website: "",
  message: "",
  website_url_2: "", // honeypot — real users never fill this
};

export default function PartnersPage() {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trustLogos, setTrustLogos] = useState([]);

  // Load current published restaurants as social proof
  useEffect(() => {
    api
      .get("/restaurants", { params: { sort: "featured" } })
      .then((r) =>
        setTrustLogos(
          (r.data || []).filter((x) => !isUnverifiedPartner(x.id)).slice(0, 12)
        )
      )
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    track("partner-form-submit-attempt", { business_type: form.business_type });
    try {
      await api.post("/partner-applications", form);
      setSubmitted(true);
      toast.success(t("partners.form.success"));
      track("partner-form-submit-success", { business_type: form.business_type });
      setForm(initialForm);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(t("partners.form.rateLimited"));
        track("partner-form-submit-rate-limited");
      } else {
        const detail = err?.response?.data?.detail || t("partners.form.error");
        toast.error(typeof detail === "string" ? detail : t("partners.form.error"));
        track("partner-form-submit-error", { status: status || "unknown" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    track("partner-hero-cta-click");
    const el = document.getElementById("partner-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="partners-page" className="min-h-screen bg-[#FDFBF7]">
      <Seo
        path="/partenaires"
        title="Devenir partenaire Vytali — L'alternative québécoise à UberEats & DoorDash"
        description="Rejoignez Vytali gratuitement. Commande en ligne + livraison partout au Québec + 5% cashback qui fait revenir vos clients. 25% de commission (vs 30% chez UberEats). Estimé +39% de revenu net."
        keywords="devenir partenaire restaurant, alternative UberEats Québec, alternative DoorDash Québec, livraison restaurant Québec, commission restaurant Québec, POS Clover Québec, marketplace restaurant Québec"
        image="https://images.unsplash.com/photo-1552566626-52f8b828add9?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200&h=630&fit=crop"
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2B2B2B] via-[#3A3A3A] to-[#1F1F1F]" />
        <div className="absolute inset-0 opacity-[0.15]" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #E63946 0%, transparent 45%), radial-gradient(circle at 80% 70%, #F4A261 0%, transparent 45%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs font-mono uppercase tracking-widest text-white/80"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#F4A261]" />
            {t("partners.hero.badge")}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 text-5xl md:text-7xl font-display font-black tracking-tighter leading-[0.95] text-white max-w-4xl"
          >
            {t("partners.hero.title1")}
            <span className="text-[#E63946]">{t("partners.hero.highlight")}</span>
            {t("partners.hero.title2")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed"
          >
            {t("partners.hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-btn"
              onClick={scrollToForm}
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#E63946] hover:bg-[#d02f3c] text-white font-bold text-base transition-all hover:scale-[1.02] hover:shadow-[6px_6px_0px_#F4A261]"
            >
              {t("partners.hero.cta")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="text-sm text-white/60 font-mono">{t("partners.hero.social")}</p>
          </motion.div>
        </div>
      </section>

      {/* Comparatif */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C5C5C] font-mono">
            {t("partners.compare.subtitle")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-display font-black tracking-tighter text-[#2B2B2B]">
            {t("partners.compare.title")}
          </h2>
        </div>

        <div className="overflow-x-auto rounded-2xl border-2 border-[#2B2B2B] bg-white shadow-[8px_8px_0px_#2B2B2B]">
          <table data-testid="compare-table" className="w-full text-left min-w-[720px]">
            <thead>
              <tr className="border-b-2 border-[#2B2B2B] bg-[#F4EFE6]">
                <th className="px-5 py-4 font-display font-bold text-sm uppercase tracking-wider">
                  {t("partners.compare.metric")}
                </th>
                <th className="px-5 py-4 font-display font-black text-sm uppercase tracking-wider text-[#E63946]">
                  Vytali
                </th>
                <th className="px-5 py-4 font-display font-bold text-sm uppercase tracking-wider text-[#5C5C5C]">
                  UberEats
                </th>
                <th className="px-5 py-4 font-display font-bold text-sm uppercase tracking-wider text-[#5C5C5C]">
                  DoorDash
                </th>
              </tr>
            </thead>
            <tbody className="text-sm">
              <CompareRow label={t("partners.compare.commission")}
                vytali="25%" uber="30%" door="30%" vytaliWin />
              <CompareRow label={t("partners.compare.cashback")}
                vytali="5% ✅" uber={<X className="w-4 h-4 inline text-[#5C5C5C]" />} door={<X className="w-4 h-4 inline text-[#5C5C5C]" />} vytaliWin />
              <CompareRow label={t("partners.compare.effective")}
                vytali={<span className="font-black">20%</span>} uber="30%" door="30%" vytaliWin />
              <CompareRow label={t("partners.compare.retention")}
                vytali="~35% 📈" uber="~15%" door="~15%" vytaliWin />
              <CompareRow label={t("partners.compare.delivery")}
                vytali={<Check className="w-4 h-4 inline text-[#2A9D8F]" />}
                uber={<Check className="w-4 h-4 inline text-[#2A9D8F]" />}
                door={<Check className="w-4 h-4 inline text-[#2A9D8F]" />} />
              <CompareRow label={t("partners.compare.support")}
                vytali={<Check className="w-4 h-4 inline text-[#2A9D8F]" />}
                uber="🟡"
                door={<X className="w-4 h-4 inline text-[#5C5C5C]" />} vytaliWin />
              <CompareRow label={t("partners.compare.multi")}
                vytali={<Check className="w-4 h-4 inline text-[#2A9D8F]" />}
                uber="🟡"
                door={<X className="w-4 h-4 inline text-[#5C5C5C]" />} vytaliWin />
              <CompareRow label={t("partners.compare.money")}
                vytali={<Check className="w-4 h-4 inline text-[#2A9D8F]" />}
                uber={<X className="w-4 h-4 inline text-[#5C5C5C]" />}
                door={<X className="w-4 h-4 inline text-[#5C5C5C]" />} vytaliWin last />
            </tbody>
          </table>
        </div>

        {/* Revenue box */}
        <div className="mt-8 rounded-2xl bg-[#2B2B2B] text-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] font-mono text-white/60">
              {t("partners.compare.revenueTitle")}
            </p>
            <p className="mt-2 text-3xl md:text-4xl font-display font-black text-[#F4A261] leading-tight">
              {t("partners.compare.revenueDelta")}
            </p>
          </div>
          <div className="border-l border-white/10 pl-6">
            <p className="text-xs uppercase tracking-widest text-white/60 font-mono">
              {t("partners.compare.revenueUber")}
            </p>
            <p className="mt-1 text-2xl font-display font-black">7 000$</p>
          </div>
          <div className="border-l border-white/10 pl-6">
            <p className="text-xs uppercase tracking-widest text-[#F4A261] font-mono">
              {t("partners.compare.revenueVytali")}
            </p>
            <p className="mt-1 text-2xl font-display font-black text-[#F4A261]">9 750$</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-[#8C8C8C] font-mono italic max-w-3xl">
          {t("partners.compare.footnote")}
        </p>
      </section>

      {/* Cashback Flywheel */}
      <section className="bg-[#F4EFE6] border-y border-[#E5E0D8] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E63946] font-mono">
              5% CASHBACK
            </p>
            <h2 className="mt-3 text-3xl md:text-5xl font-display font-black tracking-tighter text-[#2B2B2B]">
              {t("partners.flywheel.title")}
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#5C5C5C]">
              {t("partners.flywheel.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {["s1", "s2", "s3", "s4"].map((s, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative bg-white rounded-2xl p-6 border-2 border-[#2B2B2B] hover:shadow-[6px_6px_0px_#E63946] transition-shadow"
              >
                <p className="text-xs font-mono font-bold text-[#E63946] uppercase tracking-widest">
                  Étape {i + 1}
                </p>
                <h3 className="mt-2 font-display font-black text-lg text-[#2B2B2B] leading-tight">
                  {t(`partners.flywheel.${s}t`).replace(/^\d+\.\s*/, "")}
                </h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">
                  {t(`partners.flywheel.${s}d`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-5xl font-display font-black tracking-tighter text-[#2B2B2B]">
            {t("partners.how.title")}
          </h2>
          <p className="mt-3 text-base md:text-lg text-[#5C5C5C]">
            {t("partners.how.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {["s1", "s2", "s3", "s4"].map((s, i) => (
            <div key={s} className="relative">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#E63946] text-white font-display font-black text-xl mb-4">
                {i + 1}
              </div>
              <h3 className="font-display font-black text-lg text-[#2B2B2B] leading-tight">
                {t(`partners.how.${s}t`)}
              </h3>
              <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">
                {t(`partners.how.${s}d`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#2B2B2B] text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-5xl font-display font-black tracking-tighter">
              {t("partners.benefits.title")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.titleKey}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                  data-testid={`benefit-${i}`}
                  className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-colors"
                >
                  <span
                    className="inline-flex w-11 h-11 rounded-xl items-center justify-center"
                    style={{ background: b.tint }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  <h3 className="mt-4 font-display font-black text-lg leading-tight">
                    {t(b.titleKey)}
                  </h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">
                    {t(b.descKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust / social proof */}
      {trustLogos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl md:text-4xl font-display font-black tracking-tighter text-[#2B2B2B]">
              {t("partners.trust.title")}
            </h2>
            <p className="mt-2 text-sm md:text-base text-[#5C5C5C]">
              {t("partners.trust.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {trustLogos.map((r) => (
              <div
                key={r.id}
                data-testid={`trust-logo-${r.id}`}
                className="aspect-square rounded-xl overflow-hidden border border-[#E5E0D8] bg-white grayscale hover:grayscale-0 transition"
                title={r.name}
              >
                <img
                  src={r.image}
                  alt={r.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Form */}
      <section id="partner-form" className="bg-[#F4EFE6] border-y border-[#E5E0D8] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-display font-black tracking-tighter text-[#2B2B2B]">
              {t("partners.form.title")}
            </h2>
            <p className="mt-3 text-base md:text-lg text-[#5C5C5C]">
              {t("partners.form.subtitle")}
            </p>
          </div>

          {submitted ? (
            <div
              data-testid="form-success"
              className="rounded-2xl bg-white border-2 border-[#2A9D8F] p-8 text-center shadow-[6px_6px_0px_#2A9D8F]"
            >
              <CheckCircle2 className="w-14 h-14 text-[#2A9D8F] mx-auto" />
              <p className="mt-4 text-xl font-display font-black text-[#2B2B2B]">
                {t("partners.form.success")}
              </p>
              <button
                data-testid="form-reset"
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm underline text-[#5C5C5C] hover:text-[#2B2B2B]"
              >
                ↺
              </button>
            </div>
          ) : (
            <form
              data-testid="partner-form"
              onSubmit={submit}
              className="rounded-2xl bg-white border-2 border-[#2B2B2B] p-6 md:p-8 space-y-5 shadow-[8px_8px_0px_#E63946]"
            >
              {/* Business type pills */}
              <div>
                <label className="text-xs font-mono font-bold uppercase tracking-widest text-[#5C5C5C]">
                  {t("partners.form.businessType")}
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BUSINESS_TYPES.map((bt) => {
                    const Icon = bt.icon;
                    const active = form.business_type === bt.code;
                    return (
                      <button
                        type="button"
                        key={bt.code}
                        data-testid={`btype-${bt.code}`}
                        onClick={() => setForm({ ...form, business_type: bt.code })}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
                          active
                            ? "bg-[#2B2B2B] text-white border-[#2B2B2B]"
                            : "bg-white text-[#2B2B2B] border-[#E5E0D8] hover:border-[#2B2B2B]"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {t(bt.key)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <TextField
                testId="pa-business-name"
                label={t("partners.form.businessName")}
                required
                value={form.business_name}
                onChange={(v) => setForm({ ...form, business_name: v })}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  testId="pa-contact-name"
                  label={t("partners.form.contactName")}
                  required
                  value={form.contact_name}
                  onChange={(v) => setForm({ ...form, contact_name: v })}
                />
                <TextField
                  testId="pa-phone"
                  label={t("partners.form.phone")}
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
              </div>

              <TextField
                testId="pa-email"
                label={t("partners.form.email")}
                required
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <TextField
                    testId="pa-address"
                    label={t("partners.form.address")}
                    value={form.address}
                    onChange={(v) => setForm({ ...form, address: v })}
                  />
                </div>
                <TextField
                  testId="pa-city"
                  label={t("partners.form.city")}
                  value={form.city}
                  onChange={(v) => setForm({ ...form, city: v })}
                />
              </div>

              <TextField
                testId="pa-website"
                label={t("partners.form.website")}
                value={form.website}
                onChange={(v) => setForm({ ...form, website: v })}
              />

              <div>
                <label className="text-xs font-mono font-bold uppercase tracking-widest text-[#5C5C5C]">
                  {t("partners.form.message")}
                </label>
                <textarea
                  data-testid="pa-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="mt-2 w-full px-4 py-3 rounded-xl border-2 border-[#E5E0D8] bg-[#FDFBF7] outline-none focus:border-[#2B2B2B] transition-colors text-[#2B2B2B] resize-none"
                />
              </div>

              {/* Honeypot — hidden from real users, catches bots */}
              <input
                type="text"
                name="website_url_2"
                tabIndex={-1}
                autoComplete="off"
                value={form.website_url_2}
                onChange={(e) => setForm({ ...form, website_url_2: e.target.value })}
                style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
                aria-hidden="true"
              />

              <button
                type="submit"
                disabled={submitting}
                data-testid="pa-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#E63946] hover:bg-[#d02f3c] text-white font-bold text-base disabled:opacity-60 transition-all hover:scale-[1.01]"
              >
                {submitting ? t("partners.form.submitting") : t("partners.form.submit")}
                {!submitting && <Send className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-5xl font-display font-black tracking-tighter text-[#2B2B2B]">
            {t("partners.faq.title")}
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQ_KEYS.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-[#E5E0D8]">
              <AccordionTrigger
                data-testid={`faq-${i}`}
                className="text-left font-display font-bold text-base md:text-lg text-[#2B2B2B] hover:no-underline py-5"
              >
                {t(f.q)}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C5C5C] leading-relaxed pb-5">
                {t(f.a)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="bg-[#E63946] text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-display font-black tracking-tighter">
            {t("partners.finalcta.title")}
          </h2>
          <p className="mt-4 text-base md:text-lg text-white/90 max-w-xl mx-auto">
            {t("partners.finalcta.subtitle")}
          </p>
          <button
            data-testid="final-cta"
            onClick={scrollToForm}
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[#E63946] font-bold text-base hover:scale-[1.03] transition-transform hover:shadow-[6px_6px_0px_#2B2B2B]"
          >
            {t("partners.finalcta.button")}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-10 bg-[#FDFBF7]" style={{ display: "none" }} />
      <Footer />
    </div>
  );
}

// ---- sub-components ----
const CompareRow = ({ label, vytali, uber, door, vytaliWin, last }) => (
  <tr className={last ? "" : "border-b border-[#E5E0D8]"}>
    <td className="px-5 py-4 font-medium text-[#2B2B2B]">{label}</td>
    <td className={`px-5 py-4 font-bold ${vytaliWin ? "bg-[#E63946]/5 text-[#E63946]" : "text-[#2B2B2B]"}`}>
      {vytali}
    </td>
    <td className="px-5 py-4 text-[#5C5C5C]">{uber}</td>
    <td className="px-5 py-4 text-[#5C5C5C]">{door}</td>
  </tr>
);

const TextField = ({ testId, label, value, onChange, type = "text", required }) => (
  <div>
    <label className="text-xs font-mono font-bold uppercase tracking-widest text-[#5C5C5C]">
      {label}{required && <span className="text-[#E63946] ml-1">*</span>}
    </label>
    <input
      data-testid={testId}
      required={required}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full px-4 py-3 rounded-xl border-2 border-[#E5E0D8] bg-[#FDFBF7] outline-none focus:border-[#2B2B2B] transition-colors text-[#2B2B2B]"
    />
  </div>
);
